/**
 * PremiumSold
 * 
 * @Author: Focci
 * @Date: 2024-10-28 10:29:13
 * @Last Modified by: Focci
 * @Last Modified time: 2024-10-28 10:29:13
 */

'use client'

// import PropTypes from 'prop-types'
import AspectRatioCover from '@comp/AspectRatioCover'
import Link from 'next/link'
import SVGPicture from '@img/picture.svg'
import dynamic from 'next/dynamic'
import useTracking from '@hook/useTracking'
import { ButtonBase } from '@comp/material'
import { propertyDetail } from '@lib/route/house'
import { imageSize } from '@lib/image/loader'
import { useCallback, useMemo, useRef } from 'react'
import { propertyType } from '@lib/config'
import { detail } from '@lib/route/agent'
import { useLocale, useTranslations } from 'next-intl'
import PropertyCardScoff from '../../property/PropertyCardScoff'
import PropertyIcons from '../../property/PropertyIcons'
import PropertySoldDateLabel from '../../property/PropertySoldDateLabel'
import PropertyPriceLabel from '../../property/PropertyPriceLabel'
import PremiumEnquiry from './PremiumEnquiry'
import { CardAgentAvatars } from '../../property/CardAgent'

const TrackIconDialog = dynamic(() => import('@comp/page/property/estimate/track/TrackIconDialog'), { ssr: false })

export default function PremiumSold({ house, variant }) {
  const locale = useLocale()
  const enquiryDialog = useRef()
  const isMultiple = variant === 'multiple'

  const url = propertyDetail(house?.slug, locale)
  const src = useMemo(() => house?.images?.[0], [house?.images])
  const th = useTranslations('house')

  const { pushTrack } = useTracking()
  const handleEmailClick = useCallback(() => {
    enquiryDialog.current?.openDialog?.()
    
    pushTrack('click_email', {
      houseId: house.id || 0,
      agentId: house?.simple_agents?.[0]?.id,
      agencyOfficeId: house.office_id
    })
  }, [pushTrack, house])
  
  return (
    <PropertyCardScoff>
      <div className="relative">
        <Link href={url}>
          {isMultiple ? (
            <AspectRatioCover
              subtile
              unoptimized
              src={src ? imageSize(src, 864) : ''}
              width={864.66}
              height={435}
              alt={house?.address || ''}
            />
          ) : (
            <AspectRatioCover
              unoptimized
              src={src ? imageSize(src, 480, 360) : ''}
              width={480}
              height={360}
              alt={house?.address || ''}
            />
          )}
        </Link>
        <div className="absolute inset-0 z-1 flex flex-col items-center justify-center gap-2 bg-black/30 pointer-events-none">
          <span className="px-8 text-[1.6rem] font-bold text-error bg-white rounded-md">SOLD</span>
          <PropertyPriceLabel 
            priceLabel={house?.priceLabel} 
            type={propertyType.SOLD}
            className="text-white font-meium text-f1.2"
          />
          {house?.simple_agents?.[0] && (
            <div className="pointer-events-auto flex gap-2 items-center">
              <Link 
                href={detail(house?.simple_agents?.[0].slug, locale)} 
                className="inline-flex items-center h-8 px-4 text-white border border-white rounded-sm font-medium"
              >
                {th('viewAgentDetail')}
              </Link>
              <ButtonBase onClick={handleEmailClick}>
                <span className="inline-flex items-center h-8 px-4 bg-error text-white rounded-sm font-medium">
                  {th('valuationFree')}
                </span>
              </ButtonBase>
              <PremiumEnquiry 
                ref={enquiryDialog} 
                agentId={house?.simple_agents?.[0].id} 
                house={house} 
              />
            </div>
          )}
        </div>
        <div className="absolute z-[3] right-2 bottom-2 flex items-center gap-1 text-white">
          {house?.images?.length > 0 && (
            <div className="flex items-center gap-1 bg-black/60 rounded-md px-1 py-0.5">
              <SVGPicture width=".8rem" height=".6rem" />
              {house.images.length}
            </div>
          )}
        </div>
        {house?.propertyId && (
          <div className="absolute right-3 top-3 z-2">
            <TrackIconDialog propertyId={house.propertyId} />
          </div>
        )}
      </div>
      <Link 
        href={url}
        className="px-2 flex items-center justify-between h-12"
        style={{ backgroundColor: house?.officeColor, color: house?.officeFontColor }}
      >
        <CardAgentAvatars agents={house?.simple_agents} />
        <div className="w-20">
          <AspectRatioCover
            priority
            brand=""
            src={house?.officeIcon}
            width={100}
            height={30}
            className="object-contain object-right"
            alt={house?.officeName}
          />
        </div>
      </Link>
      <Link className="block p-4" href={url}>
        {house?.soldData && <PropertySoldDateLabel
          soldData={house.soldData}
          className="text-error font-medium text-f.6"
        />}
        {house?.propertyIcon && <PropertyIcons
          iconWidth=".8rem" 
          data={house.propertyIcon} 
        />}
        {house?.address && (
          <div className="line-clamp-1 text-f1.8 font-medium">{house.address}</div>
        )}
      </Link>
    </PropertyCardScoff>
  )
}

// PremiumSold.propTypes = {
//   className: PropTypes.string,
// }
